import { Globe2, Lock, Star } from "lucide-react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { FieldError } from "../common/States";
import { CATEGORY } from "../../lib/labels";
import { useReference } from "../../lib/reference";

export const VisibilityPicker = ({ value, onChange, forcedPrivate }) => (
  <div className="grid gap-2 sm:grid-cols-2" data-testid="visibility-picker">
    {[["public", Globe2, "Avis public", "Publié après validation par l'organisation, sans vos coordonnées."],
      ["private", Lock, "Message privé", "Réclamation ou signalement lu uniquement par l'organisation."]].map(([k, Icon, t, d]) => {
      const active = value === k;
      const disabled = forcedPrivate && k === "public";
      return (
        <button type="button" key={k} disabled={disabled} onClick={() => onChange(k)} data-testid={`visibility-${k}`}
          className={`rounded-xl border p-4 text-left transition-colors ${active ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600" : "border-slate-200 bg-white hover:border-slate-300"} ${disabled ? "cursor-not-allowed opacity-50" : ""}`}>
          <span className="flex items-center gap-2 font-medium"><Icon className="h-4 w-4 text-blue-600" />{t}</span>
          <span className="mt-1 block text-xs text-slate-500">{d}</span>
        </button>
      );
    })}
  </div>
);

export const RatingInput = ({ value, onChange }) => (
  <div className="flex gap-1" data-testid="rating-input">
    {[1, 2, 3, 4, 5].map((i) => (
      <button type="button" key={i} onClick={() => onChange(i)} data-testid={`rating-star-${i}`} aria-label={`${i} étoile(s)`}
        className="rounded-md p-1 transition-transform hover:scale-110">
        <Star className={`h-9 w-9 ${i <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      </button>
    ))}
  </div>
);

export const CategoryPicker = ({ value, onChange, extra = [] }) => {
  const ref = useReference();
  const base = ref ? ref.categories.map((c) => [c.code, c.label]) : Object.entries(CATEGORY);
  const items = [...extra.map((c) => [c.code, c.label]), ...base.filter(([k]) => !extra.some((c) => c.code === k))];
  return (
  <div className="flex flex-wrap gap-2" data-testid="category-picker">
    {items.map(([k, l]) => (
      <button type="button" key={k} onClick={() => onChange(k)} data-testid={`category-${k}`}
        className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${value === k ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}>
        {l}
      </button>
    ))}
  </div>
  );
};

export const IdentityBlock = ({ f, set, errors, allowAnonymous }) => (
  <div className="space-y-4 rounded-xl border border-slate-200 p-4">
    {allowAnonymous && (
      <label className="flex items-center justify-between gap-4">
        <span className="text-sm"><b>Rester anonyme</b><br /><span className="text-slate-500">Suivi possible grâce à la référence et au code secret.</span></span>
        <Switch checked={f.anonymous} onCheckedChange={set("anonymous")} data-testid="feedback-anonymous-switch" />
      </label>
    )}
    {!f.anonymous && (
      <div className="grid gap-3 sm:grid-cols-3">
        <div><Label className="sr-only">Nom</Label><Input placeholder="Nom complet" value={f.contact_name} onChange={(e) => set("contact_name")(e.target.value)} data-testid="feedback-contact-name-input" /><FieldError>{errors.contact_name}</FieldError></div>
        <Input type="email" placeholder="Email" value={f.contact_email} onChange={(e) => set("contact_email")(e.target.value)} data-testid="feedback-contact-email-input" />
        <Input type="tel" placeholder="Téléphone" value={f.contact_phone} onChange={(e) => set("contact_phone")(e.target.value)} data-testid="feedback-contact-phone-input" />
        <div className="sm:col-span-3"><FieldError>{errors.contact}</FieldError><p className="text-xs text-slate-500">Vos coordonnées sont transmises uniquement à l'organisation et ne sont jamais publiées.</p></div>
      </div>
    )}
  </div>
);
